/**
 * Per-preset affordance metadata for the /try create-module tabs.
 *
 * WHAT THIS FILE DOES:
 * - Owns the placeholder, helper, inline hint, file-controls emphasis flag,
 *   and post-switch focus target for each of the five tab presets.
 * - Pure data + a typed lookup helper. No DOM, no event wiring, no submit
 *   logic — those live in `try-ui.ts` (which calls `applyCreatePreset(mode)`).
 *
 * WHY THIS EXISTS:
 * - Centralizes the educational copy so a content edit is one diff in one
 *   file (david-ogilvy persona owns the wording).
 * - Keeps `try-ui.ts` from growing — the preset table would otherwise pad
 *   the controller toward the 1k-LOC ceiling.
 *
 * NON-GOALS:
 * - Submit-side validation: tabs are educational presets only, the existing
 *   `/try` submit pipeline is the single source of truth.
 * - URL detection or routing: the server decides what a URL means.
 *
 * RELATED FILES:
 * - src/client/try-mode-tabs.ts (mode identifiers + tab bar)
 * - src/client/try-ui.ts (consumer; updates DOM via these presets)
 * - src/ssr/try.ts (renders #try-mode-hint and #try-file-pills slots)
 */

import { TryModes, type TryMode } from './try-mode-tabs.js';

/**
 * Element to focus after a *user-initiated* preset switch.
 *
 * `null` means "leave focus alone" — used for programmatic switches like the
 * drop handler (which forces File mode after the file is already staged).
 */
export type TryModeFocusTarget = 'textarea' | 'upload' | null;

export interface TryModePreset {
  /** Placeholder for #try-textarea. */
  placeholder: string;
  /** Helper copy that replaces #try-helper for this preset. */
  helper: string;
  /**
   * Optional inline hint shown inside #try-mode-hint. Empty string hides the
   * hint entirely. Kept short — david-ogilvy persona owns wording.
   */
  hint: string;
  /**
   * When true, the create-module wrapper gains
   * `data-file-controls-primary="true"` so CSS can give the upload button +
   * file-type pills more visual weight (currently the File preset only).
   */
  fileControlsPrimary: boolean;
  /** See `TryModeFocusTarget`. */
  focusTarget: TryModeFocusTarget;
}

/**
 * Canonical preset table.
 *
 * Wording is intentionally short and concrete:
 * - Tell the user what to do, not what the system supports.
 * - Hints carry the one extra fact the placeholder can't ("playlists may
 *   take longer", "private docs without share access can't be read").
 */
export const TryModePresets: Readonly<Record<TryMode, TryModePreset>> = Object.freeze({
  [TryModes.TEXT]: {
    placeholder: 'Type or paste the text you want summarized.',
    helper: 'Plain text, notes, or anything you can write down.',
    hint: '',
    fileControlsPrimary: false,
    focusTarget: 'textarea'
  },
  [TryModes.URL]: {
    placeholder: 'Paste a link.',
    helper: 'Articles, blog posts, papers — almost any URL.',
    hint: '',
    fileControlsPrimary: false,
    focusTarget: 'textarea'
  },
  [TryModes.FILE]: {
    placeholder: 'Drop a file here, or use the upload button below.',
    helper: 'PDF, Markdown, audio, or video. Up to 1.5 GB.',
    hint: '',
    fileControlsPrimary: true,
    focusTarget: 'upload'
  },
  [TryModes.YOUTUBE]: {
    placeholder: 'Paste a YouTube link.',
    helper: 'Single videos work best.',
    hint: 'Playlists work but may take longer.',
    fileControlsPrimary: false,
    focusTarget: 'textarea'
  },
  [TryModes.GOOGLEDOC]: {
    placeholder: 'Paste a Google Doc link.',
    helper: 'Make sure the doc is public or shared with anyone with the link.',
    hint: "Private docs without share access can't be read.",
    fileControlsPrimary: false,
    focusTarget: 'textarea'
  }
});

